<template>
  <div class="w-full max-w-sm mx-auto mt-16 mb-16">
    <div class="text-center mb-6">
      <h2 class="text-2xl font-bold text-gray-800 mb-1">eRegistry Admin Portal</h2>
      <p class="text-sm text-gray-500">Password Recovery</p>
    </div>

    <!-- Main Reset Password Card -->
    <div class="bg-white rounded-xl shadow-md border border-gray-100 p-6 sm:p-8">
      <!-- Reset Request Form -->
      <form v-if="!resetRequested" @submit.prevent="onResetPasswordSubmit" class="space-y-4">
        <p class="text-sm text-gray-600 text-center">
          Enter your account's email address and we'll send you a password reset link.
        </p>

        <!-- Error Message -->
        <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600 font-medium text-center">
            {{ errorMessage }}
          </p>
        </div>

        <!-- Email Input -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 mb-1.5">
            Email Address
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="fa-solid fa-envelope text-gray-400 text-sm"></i>
            </div>
            <input
              id="email"
              v-model="form.userEmail"
              type="email"
              required
              placeholder="Enter your email address"
              class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary cursor-text"
            />
          </div>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="showSpinner"
          class="w-full py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-700 transition font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          <template v-if="!showSpinner">
            <i class="fa-solid fa-paper-plane"></i>
            Send Reset Link
          </template>
          <template v-else>
            <i class="fa-solid fa-spinner fa-spin"></i>
            Sending...
          </template>
        </button>

        <!-- Back to Login Link -->
        <div class="text-center">
          <p class="text-sm text-gray-600">
            Remember your password?
            <router-link
              :to="{ name: 'login' }"
              class="font-medium text-primary hover:text-primary-700 hover:underline ml-1 cursor-pointer">
              Back to Login
            </router-link>
          </p>
        </div>
      </form>

      <!-- Success State -->
      <div v-else class="space-y-4 text-center">
        <i class="fa-solid fa-circle-check text-4xl text-green-500"></i>
        <p class="text-sm text-gray-700 leading-relaxed">
          If an account exists for <span class="font-semibold">{{ form.userEmail }}</span>,
          a password reset link has been sent. Please check your inbox.
        </p>
        <router-link
          :to="{ name: 'login' }"
          class="inline-flex items-center justify-center gap-2 w-full py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-700 transition font-semibold cursor-pointer">
          <i class="fa-solid fa-right-to-bracket"></i>
          Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useLoginStore } from "@/stores/login";
import { useToast } from "@/composables";

// Types
interface ResetForm {
  userEmail: string | null
}

// Stores and Composables
const loginStore = useLoginStore()
const { successToast, errorToast } = useToast()

// Store state
const { getRequestPassword, getLoginError } = storeToRefs(loginStore)

// Reactive state
const showSpinner = ref<boolean>(false)
const resetRequested = ref<boolean>(false)
const errorMessage = ref<string | null>(null)

const form = ref<ResetForm>({
  userEmail: null,
})

// Methods
const onResetPasswordSubmit = () => {
  if (showSpinner.value) return
  errorMessage.value = null
  showSpinner.value = true
  loginStore.requestResetCode(form.value)
    .finally(() => {
      showSpinner.value = false
    })
}

// Watchers
watch(getRequestPassword, (data) => {
  if (data) {
    successToast("Success", data.message || "Reset link sent")
    resetRequested.value = true
  }
})

watch(getLoginError, (data) => {
  if (data) {
    showSpinner.value = false
    errorToast("Error", data.message)
    errorMessage.value = data.message
  }
})
</script>
